import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { StatsRepoService } from '@client/app/services/api/stats-repo/stats-repo.service';
import { SessionService } from '@monsido/core/session/session.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { StateService } from '@uirouter/core';
import moment from 'moment';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MON_ICONS } from '@layout/icons/icons.constants';
import { angularForEach } from '@client/ng2/shared/utils';

export interface StatsSummaryMostFrequentData {
    mostFrequentPages: any[];
    totalPages: number;
    avgPageViews: number;
    topPage: any;
}

@Component({
    selector: 'statistics-summary-most-frequent',
    templateUrl: './most-frequent.component.html',
    standalone: false,
})
export class StatisticsSummaryMostFrequentComponent implements OnInit, OnChanges {
    @Input() startDate: any = moment();
    @Input() endDate: any = moment();

    summary: StatsSummaryMostFrequentData = {
        mostFrequentPages: [],
        totalPages: 0,
        avgPageViews: 0,
        topPage: null,
    };

    mostFrequent: any = {};
    loading = false;
    allEmpty = false;
    emptyTypes: any[] = [];
    types = ['from', 'using', 'browser', 'os', 'resolution', 'language'];
    page = 1;

    constructor (
        private statsRepoService: StatsRepoService,
        public sessionService: SessionService,
        public pipeService: PipeService,
        private stateService: StateService,
        private translateService: TranslateService,
    ) {}

    ngOnInit () {
        this.allEmpty = false;
    }

    ngOnChanges (): void {
        this.mostFrequent = {};
        this.loading = true;
        const promises: any[] = [];
        const typeTranslations = {
            from: this.translateService.getString('Location'),
            using: this.translateService.getString('Device'),
            browser: this.translateService.getString('Browser'),
            os: this.translateService.getString('Operating System'),
            resolution: this.translateService.getString('Resolution'),
            language: this.translateService.getString('Language'),
        };
        const params = {
            limit: 100,
            page: this.page || 1,
            domainId: this.stateService.params.domainId,
            from: this.startDate.format('YYYY-MM-DD'),
            to: this.endDate.format('YYYY-MM-DD'),
        };
        promises.push(this.getLocations(params));
        promises.push(this.getDevices(params));
        promises.push(this.getBrowsers(params));
        promises.push(this.getOs(params));
        promises.push(this.getResolutions(params));
        promises.push(this.getLanguages(params));

        Promise.all(promises)
            .then(
                (data) => {
                    this.emptyTypes = [];
                    this.types.map((type, index) => {
                        this.mostFrequent[type] = {
                            type: type,
                            title: typeTranslations[type],
                        };

                        if (data[index][0]) {
                            this.mostFrequent[type].label = data[index][0].label || '';
                            this.mostFrequent[type].value = data[index][0].visits || 0;
                            this.mostFrequent[type].total = data[index].reduce((total, page) => {
                                return total + parseInt(page.visits);
                            }, 0);
                        } else {
                            this.emptyTypes.push(type);
                            this.mostFrequent[type].label = this.translateService.getString('Unknown');
                            this.mostFrequent[type].value = 1;
                            this.mostFrequent[type].total = 1;
                        }
                    });

                    this.allEmpty = this.emptyTypes.length === data.length;
                },
                () => {
                    this.allEmpty = true;
                },
            )
            .finally(() => {
                this.loading = false;
            });
    }

    getLocations (params) {
        return this.statsRepoService.getLocations(params);
    }

    getDevices (params) {
        return this.statsRepoService.getDevices(params);
    }

    getBrowsers (params) {
        return this.statsRepoService.getBrowsers(params);
    }

    getOs (params) {
        return this.statsRepoService.getOs(params);
    }

    getResolutions (params) {
        return this.statsRepoService.getResolutions(params);
    }

    getLanguages (params) {
        return this.statsRepoService.getLanguages(params);
    }

    mostFrequentClass (data) {
        let result;
        switch (data.type) {
            case 'browser':
                angularForEach(MON_ICONS.STATISTICS.SOURCES.BROWSERS, (icon, browser) => {
                    if (data.label.toLowerCase().indexOf(browser) > -1) {
                        result = icon;
                    }
                });
                break;
            case 'using':
                angularForEach(MON_ICONS.STATISTICS.SOURCES.DEVICES, (icon, device) => {
                    if (data.label.toLowerCase().indexOf(device) > -1) {
                        result = icon;
                    }
                });
                break;
            case 'os':
                angularForEach(MON_ICONS.STATISTICS.SOURCES.OPERATION_SYSTEMS, (icon, os) => {
                    if (data.label.toLowerCase().indexOf(os) > -1) {
                        result = icon;
                    }
                });
                break;
            default:
                result = MON_ICONS.STATISTICS.SOURCES[data.type.toUpperCase()];
                break;
        }

        if (typeof result === 'undefined') {
            result = MON_ICONS.STATISTICS.SOURCES.UNKNOWN;
        }

        return result;
    }
}
